import React from 'react';
import './FocusCatcher.css';
import GameCanvas from '../components/game/GameCanvas';
import GameHUD from '../components/game/GameHUD';
import GameResults from '../components/game/GameResults';
import GameLevelSelect from '../components/game/GameLevelSelect';
import GameEngine from '../tools/gameEngine';
import gameAudio from '../tools/gameAudio';
import { getLevel, getLevelCount } from '../tools/gameLevels';
import { getGameData, saveGameData, recordGameSession, isLevelUnlocked, calculateStars, getTheoreticalMax } from '../tools/gameProgress';
import ProgressTracking from '../tools/progressTracking';
import { getAudioEnabled } from '../tools/localStorage';
import wakeLockManager from '../tools/WakeLock';

const SCREEN_LEVEL_SELECT = 'levelSelect';
const SCREEN_PLAYING = 'playing';
const SCREEN_RESULTS = 'results';

class FocusCatcher extends React.Component {
    constructor(props) {
        super(props);
        this.engine = new GameEngine();
        this.state = {
            screen: SCREEN_LEVEL_SELECT,
            currentLevel: null,
            score: 0,
            combo: 0,
            wave: null,
            paused: false,
            gameStats: null,
            tutorialHitCount: 0,
        };
    }

    componentDidMount() {
        document.addEventListener('visibilitychange', this._handleVisibilityChange);
        // Prevent pull-to-refresh and scroll during gameplay
        document.addEventListener('touchmove', this._preventScroll, { passive: false });
    }

    componentWillUnmount() {
        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        document.removeEventListener('touchmove', this._preventScroll);
        wakeLockManager.release();
    }

    _handleVisibilityChange = () => {
        if (document.hidden && this.state.screen === SCREEN_PLAYING && !this.state.paused) {
            this._togglePause();
        }
    }

    _preventScroll = (e) => {
        if (this.state.screen === SCREEN_PLAYING) {
            e.preventDefault();
        }
    }

    _startLevel = (levelId) => {
        gameAudio.setEnabled(getAudioEnabled());
        gameAudio.unlock();
        this.engine.startLevel(levelId);
        wakeLockManager.acquire();
        this.setState({
            screen: SCREEN_PLAYING,
            currentLevel: levelId,
            score: 0,
            combo: 0,
            wave: null,
            paused: false,
            gameStats: null,
            tutorialHitCount: 0,
        });

        // Expose game state for testing
        window.__gameState = {
            decoySpawned: false,
            goldenSpawned: false,
            movingSpawned: false,
            ddaModifier: 1.0,
        };
    }

    _togglePause = () => {
        const newPaused = !this.state.paused;
        this.engine.paused = newPaused;
        this.setState({ paused: newPaused });
    }

    _handleGameUpdate = (update) => {
        if (update.isComplete && this.state.screen === SCREEN_PLAYING) {
            this._handleLevelComplete();
            return;
        }

        // Expose game state for testing
        if (window.__gameState) {
            window.__gameState.ddaModifier = this.engine.ddaModifier;
            if (this.engine._decoySpawned) window.__gameState.decoySpawned = true;
            if (this.engine._goldenSpawned) window.__gameState.goldenSpawned = true;
            if (this.engine._movingSpawned) window.__gameState.movingSpawned = true;
        }

        this.setState({
            score: update.score,
            combo: update.combo,
            wave: update.progress.wave,
            timeRemaining: update.progress.timeRemaining,
            tutorialHitCount: update.tutorialHitCount,
        });
    }

    _handleLevelComplete() {
        const stats = this.engine.getStats();
        const levelId = this.state.currentLevel;
        const gameData = getGameData();
        const isEndless = levelId === 'endless';
        const isDaily = levelId === 'daily';
        const isSpecialMode = isEndless || isDaily;

        const previousBest = isEndless
            ? (gameData.endlessHighScore || 0)
            : (gameData.levelHighScores[levelId] || 0);
        const isNewRecord = stats.score > previousBest;

        // Record to game progress (returns newly earned achievements)
        const { newAchievements } = recordGameSession(levelId, stats);

        // Record daily challenge completion (already handled in recordGameSession)


        // Record to main progress tracking (counts toward streaks)
        ProgressTracking.recordSession('focusCatcher');

        wakeLockManager.release();

        const gamesPlayed = getGameData().totalGamesPlayed || 0;

        this.setState({
            screen: SCREEN_RESULTS,
            gameStats: {
                ...stats,
                previousBest,
                isNewRecord,
                levelId,
                stars: isSpecialMode ? 0 : calculateStars(stats.score, getTheoreticalMax(levelId), stats.accuracy),
                newAchievements: newAchievements || [],
                isEndless,
                isDaily,
                consecutiveGames: gamesPlayed,
            },
        });
    }

    _handleRetry = () => {
        this._startLevel(this.state.currentLevel);
    }

    _handleNextLevel = () => {
        const current = this.state.currentLevel;
        // Endless/daily don't have "next level"
        if (typeof current !== 'number') {
            this.setState({ screen: SCREEN_LEVEL_SELECT });
            return;
        }
        const next = current + 1;
        if (next <= getLevelCount() && isLevelUnlocked(next)) {
            this._startLevel(next);
        } else {
            this.setState({ screen: SCREEN_LEVEL_SELECT });
        }
    }

    _handleQuit = () => {
        wakeLockManager.release();
        this.setState({ screen: SCREEN_LEVEL_SELECT });
    }

    _handleBack = () => {
        window.location.href = '/welcome';
    }

    render() {
        const { screen, currentLevel, score, combo, wave, timeRemaining, paused, gameStats, tutorialHitCount } = this.state;

        if (screen === SCREEN_LEVEL_SELECT) {
            return (
                <div className="focus-catcher-page" data-testid="focus-catcher-page">
                    <GameLevelSelect
                        onSelectLevel={this._startLevel}
                        onBack={this._handleBack}
                    />
                </div>
            );
        }

        if (screen === SCREEN_RESULTS && gameStats) {
            const level = getLevel(currentLevel);
            const isSpecialMode = currentLevel === 'endless' || currentLevel === 'daily';
            const nextLevelExists = !isSpecialMode && currentLevel < getLevelCount();
            const nextUnlocked = nextLevelExists && isLevelUnlocked(currentLevel + 1);

            return (
                <div className="focus-catcher-page results-page" data-testid="focus-catcher-page">
                    <GameResults
                        levelId={currentLevel}
                        levelName={level.name}
                        stats={gameStats}
                        stars={gameStats.stars}
                        previousBest={gameStats.previousBest}
                        isNewRecord={gameStats.isNewRecord}
                        hasNextLevel={nextLevelExists && nextUnlocked}
                        onRetry={this._handleRetry}
                        onNextLevel={this._handleNextLevel}
                        onQuit={this._handleQuit}
                    />
                </div>
            );
        }

        // Playing screen
        const level = getLevel(currentLevel);
        const isTutorial = level && level.istutorial;

        return (
            <div className="focus-catcher-page game-active" data-testid="focus-catcher-page">
                <div className="game-container" data-testid="game-container">
                    <GameCanvas
                        engine={this.engine}
                        onUpdate={this._handleGameUpdate}
                        onTap={() => {}}
                    />
                    <GameHUD
                        score={score}
                        combo={combo}
                        wave={wave}
                        timeRemaining={timeRemaining}
                        levelName={level ? level.name : ''}
                        paused={paused}
                        onPause={this._togglePause}
                        onQuit={this._handleQuit}
                        isTutorial={isTutorial}
                        tutorialHitCount={tutorialHitCount}
                    />
                </div>
            </div>
        );
    }
}

export default FocusCatcher;
